import React from 'react';

interface DotRowProps {
  filledCount?: number;
  primaryColor: string;
  glowColor: string;
  rowIndex: number;
  maxCount: number;
}

export const DotRow: React.FC<DotRowProps> = ({ filledCount = 0, primaryColor, glowColor, rowIndex, maxCount }) => {
  const dots = Array.from({ length: maxCount }, (_, i) => i);
  const isRowComplete = filledCount >= maxCount;

  return (
    <div
      className="tw-relative tw-flex tw-items-center tw-justify-between tw-px-4"
      style={{
        paddingTop: 'var(--spacing-1)',
        paddingBottom: 'var(--spacing-1)',
        position: 'relative',
        // Add extra space for glow to prevent clipping
        marginTop: 'var(--spacing-2)',
        marginBottom: 'var(--spacing-2)',
      }}
    >
      {/* Complete row glow effect */}
      {isRowComplete && (
        <>
          {/* Pulsing glow behind the entire row - extends beyond container */}
          <div
            className="tw-absolute tw-pointer-events-none"
            style={{
              top: '-16px',
              bottom: '-16px',
              left: '-16px',
              right: '-16px',
              background: `radial-gradient(ellipse at center, ${glowColor}30 0%, transparent 70%)`,
              animation: 'rowCompletePulse 2s ease-in-out infinite',
              filter: 'blur(12px)',
              borderRadius: '100px',
            }}
          />

          {/* Shimmer effect that sweeps across the row */}
          <div
            className="tw-absolute tw-inset-0 tw-pointer-events-none tw-overflow-hidden"
            style={{
              borderRadius: '100px',
            }}
          >
            <div
              className="tw-absolute tw-inset-0"
              style={{
                background: `linear-gradient(90deg, transparent 0%, transparent 40%, ${glowColor}80 50%, transparent 60%, transparent 100%)`,
                animation: 'rowShimmer 3s linear infinite',
                width: '300%',
                left: '-200%',
              }}
            />
          </div>
        </>
      )}

      {dots.map(dotIndex => {
        const isFilled = dotIndex < filledCount;
        const isLastFilled = dotIndex === filledCount - 1;
        const hasNextDot = dotIndex < maxCount - 1;
        const nextDotFilled = dotIndex + 1 < filledCount;

        return (
          <React.Fragment key={`${rowIndex}-${dotIndex}`}>
            {/* Dot */}
            <div className="tw-relative tw-z-10">
              <div
                className={`tw-w-4 tw-h-4 tw-rounded-full tw-transition-all tw-duration-500 ${
                  isFilled ? `${primaryColor} tw-shadow-lg` : 'tw-bg-gray-300 dark:tw-bg-gray-700'
                }`}
                style={
                  isFilled
                    ? {
                        boxShadow: isRowComplete
                          ? `0 0 16px ${glowColor}, 0 0 32px ${glowColor}60, 0 0 48px ${glowColor}30`
                          : `0 0 12px ${glowColor}, 0 0 24px ${glowColor}40`,
                      }
                    : {}
                }
              >
                {/* Pulse animation on last filled dot (only if row not complete) */}
                {isLastFilled && !isRowComplete && (
                  <div
                    className="tw-absolute tw-inset-0 tw-rounded-full tw-animate-ping"
                    style={{ backgroundColor: glowColor, opacity: 0.3 }}
                  />
                )}
              </div>
            </div>

            {/* Connecting Line */}
            {hasNextDot && (
              <div className="tw-flex-1 tw-mx-1">
                <div
                  className={`tw-h-0.5 tw-transition-all tw-duration-700 ${
                    nextDotFilled ? `${primaryColor}` : 'tw-bg-gray-300 dark:tw-bg-gray-700'
                  }`}
                  style={
                    nextDotFilled
                      ? {
                          boxShadow: isRowComplete
                            ? `0 0 12px ${glowColor}80, 0 0 24px ${glowColor}40`
                            : `0 0 8px ${glowColor}60`,
                        }
                      : {}
                  }
                />
              </div>
            )}
          </React.Fragment>
        );
      })}

      {/* Keyframe animations */}
      <style>{`
        @keyframes rowCompletePulse {
          0%, 100% { 
            opacity: 0.3;
            transform: scale(1);
          }
          50% { 
            opacity: 0.6;
            transform: scale(1.05);
          }
        }
        
        @keyframes rowShimmer {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(100%);
          }
        }
      `}</style>
    </div>
  );
};
